import { ACTIVE_KID, PUBLIC_KEYS, type PublicJwk } from "../keys.ts";

/** base64url encode bytes (no padding). */
function b64url(buf: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const enc = new TextEncoder();

async function importPublicKey(jwk: PublicJwk): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "jwk",
    { ...jwk, alg: "RSA-OAEP-256", ext: true },
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
}

/**
 * Seal a JSON payload into a compact JWE:
 *   BASE64URL(header).BASE64URL(encKey).BASE64URL(iv).BASE64URL(ciphertext).BASE64URL(tag)
 * - CEK: random AES-256-GCM key, wrapped with RSA-OAEP-256.
 * - AAD: the ASCII bytes of the encoded protected header (per RFC 7516).
 */
export async function sealJwe(payload: unknown, kid: string = ACTIVE_KID): Promise<string> {
  const jwk = PUBLIC_KEYS[kid];
  if (!jwk) throw new Error(`unknown kid: ${kid}`);

  const header = { alg: "RSA-OAEP-256", enc: "A256GCM", kid, typ: "GSDS" };
  const encodedHeader = b64url(enc.encode(JSON.stringify(header)));
  const aad = enc.encode(encodedHeader);

  // Content-encryption key
  const cek = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt"]);
  const rawCek = new Uint8Array(await crypto.subtle.exportKey("raw", cek));

  // Wrap CEK with RSA-OAEP-256
  const pub = await importPublicKey(jwk);
  const wrapped = new Uint8Array(await crypto.subtle.encrypt({ name: "RSA-OAEP" }, pub, rawCek as BufferSource));

  // Encrypt the plaintext
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = enc.encode(JSON.stringify(payload));
  const ctTag = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: aad, tagLength: 128 },
      cek,
      plaintext as BufferSource,
    ),
  );
  const ciphertext = ctTag.slice(0, ctTag.length - 16);
  const tag = ctTag.slice(ctTag.length - 16);

  return [encodedHeader, b64url(wrapped), b64url(iv), b64url(ciphertext), b64url(tag)].join(".");
}
